import React, { useState, useEffect } from "react";
import { Card, Select, DatePicker, Button, Space, Radio, Form, message } from "antd";
import { FilterOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getMediaPlans, getCashFlowCampaigns, getCashFlowProducts, getCashFlowProductCategories } from "../../../core/api/mediaplan";
import { fetchBrandsBySelectedClients } from "../../../core/api/settings";
import { getProductsByBrand, getProductUniverses } from "../../../core/api/products";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;

const CashFlowFilters = ({ filters = {}, setFilters, onApplyFilters = () => { 
  //intentionally left empty
 }, onClearFilters = () => {
  //intentionally left empty
 }, setFiltersMinimized: setParentFiltersMinimized }) => {
  const [loadingFilter, setLoadingFilter] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [productCategoryOptions, setProductCategoryOptions] = useState([]);
  const [filtersMinimized, setLocalFiltersMinimized] = useState(() => {
    // Get the saved state from localStorage or default to false if not found
    const savedState = localStorage.getItem('cashFlowFiltersMinimized');
    return savedState ? JSON.parse(savedState) : false;
  });
  
  // Function to update both local and parent state
  const setFiltersMinimized = (value) => {
    setLocalFiltersMinimized(value);
    if (setParentFiltersMinimized) {
      setParentFiltersMinimized(value);
    }
  };
  const selectedClients = useSelector((state) => state.selectedClients);
  
  // Effect to sync localStorage with state changes
  useEffect(() => {
    localStorage.setItem('cashFlowFiltersMinimized', JSON.stringify(filtersMinimized));
  }, [filtersMinimized]);

  // Set default filters when component mounts
  useEffect(() => {
    if (!filters.brandIds) {
      setFilters(prev => ({
        ...prev,
        brandIds: [],
        productIds: [],
        productCategoryIds: [],
        campaignIds: [],
        dateRange: [dayjs(), dayjs().add(30, "days")],
        timePeriod: "daily"
      }));
    }
  }, []);
  
  // Fetch product categories (universes) based on selected brands, products, and campaigns
  useEffect(() => {
    const fetchProductCategories = async () => {
      // Only fetch categories if brands are selected and date range is set
      if (filters?.brandIds?.length > 0 && filters?.dateRange?.length === 2) {
        try {
          setLoadingFilter('productCategories');
          
          // Use the new API endpoint to fetch product categories based on selected filters
          const response = await getCashFlowProductCategories({
            brandIds: filters.brandIds,
            productIds: filters.productIds || [],
            campaignIds: filters.campaignIds || [],
            dateRange: filters.dateRange
          });
          
          if (response && response.data) {
            const categoryOptions = response.data.map(category => ({
              label: `${category.name}`,
              value: category.id
            }));
            setProductCategoryOptions(categoryOptions);
            
            // If current selected categories are not in the new options, clear the selection
            if (filters.productCategoryIds?.length > 0) {
              const validCategoryIds = categoryOptions.map(opt => opt.value);
              const validSelectedCategoryIds = filters.productCategoryIds.filter(id => 
                validCategoryIds.includes(id)
              );
              
              if (validSelectedCategoryIds.length !== filters.productCategoryIds.length) {
                handleFilterChange('productCategoryIds', validSelectedCategoryIds);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching product categories:', error);
          message.error('Failed to load product category options');
        } finally {
          setLoadingFilter(null);
        }
      } else if (!filters?.dateRange || filters.dateRange.length !== 2) {
        // If no date range is set, use the standard API to get all categories
        try {
          setLoadingFilter('productCategories');
          const response = await getProductUniverses();
          
          if (response && response.data) {
            const categoryOptions = response.data.map(universe => ({
              label: universe.name,
              value: universe.id
            }));
            setProductCategoryOptions(categoryOptions);
          }
        } catch (error) {
          console.error('Error fetching product categories:', error);
          message.error('Failed to load product category options');
        } finally {
          setLoadingFilter(null);
        }
      } else {
        // Clear category options if no brands are selected
        setProductCategoryOptions([]);
        // Also clear any selected categories
        if (filters.productCategoryIds?.length > 0) {
          handleFilterChange('productCategoryIds', []);
        }
      }
    };
    
    fetchProductCategories();
  }, [filters.brandIds, filters.productIds, filters.campaignIds, filters.dateRange]);

  // Fetch brands when selected clients change
  useEffect(() => {
    const fetchBrands = async () => {
      if (selectedClients && selectedClients.length > 0) {
        try {
          setLoadingFilter('brands');
          
          // Pass the selectedClients array to the backend
          const response = await fetchBrandsBySelectedClients(selectedClients);
          
          if (response && response.data) {
            // Transform the data to the format expected by the Select component
            const brandOptions = response.data.map(brand => ({
              label: brand.brand_name,
              value: brand.id
            }));
            setBrandOptions(brandOptions);
            if (brandOptions.length === 1) {
              handleFilterChange('brandIds', [brandOptions[0].value]);
            }
          }
        } catch (error) {
          console.error('Error fetching brands:', error);
          message.error('Failed to load brand options');
        } finally {
          setLoadingFilter(null);
        }
      } else {
        setBrandOptions([]);
      }
    };
    
    fetchBrands();
  }, [selectedClients]);

  // Fetch products when brands, product categories, campaigns, or date range change
  useEffect(() => {
    const fetchProducts = async () => {
      // Only fetch products if brands are selected and date range is set
      if (filters?.brandIds?.length > 0 && filters?.dateRange?.length === 2) {
        try {
          setLoadingFilter('products');
          
          // Use the new API endpoint to fetch products based on selected filters
          const response = await getCashFlowProducts({
            brandIds: filters.brandIds,
            productCategoryIds: filters.productCategoryIds || [],
            campaignIds: filters.campaignIds || [],
            dateRange: filters.dateRange
          });
          
          if (response && response.data) {
            const productOptions = response.data.map(product => ({
              label: product.name,
              value: product.id,
              category: product.product_universe_name
            }));
            
            setProductOptions(productOptions);
            
            // If current selected products are not in the new options, clear the selection
            if (filters.productIds?.length > 0) {
              const validProductIds = productOptions.map(opt => opt.value);
              const validSelectedProductIds = filters.productIds.filter(id => 
                validProductIds.includes(id)
              );
              
              if (validSelectedProductIds.length !== filters.productIds.length) {
                handleFilterChange('productIds', validSelectedProductIds);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching products:', error);
          message.error('Failed to load product options');
        } finally {
          setLoadingFilter(null);
        }
      } else {
        // Clear product options if no brands are selected
        setProductOptions([]);
        // Also clear any selected products
        if (filters.productIds?.length > 0) {
          handleFilterChange('productIds', []);
        }
      }
    };

    fetchProducts();
  }, [filters.brandIds, filters.productCategoryIds, filters.campaignIds, filters.dateRange]);

  // Fetch campaigns when brands, products, product categories, or date range changes
  useEffect(() => {
    const fetchCampaigns = async () => {
      // Only fetch campaigns if brands are selected and date range is set
      if (filters?.brandIds?.length > 0 && filters?.dateRange?.length === 2) {
        try {
          setLoadingFilter('campaigns');
          
          // Use the new API endpoint to fetch campaigns based on selected filters
          const response = await getCashFlowCampaigns({
            brandIds: filters.brandIds,
            productIds: filters.productIds || [],
            productCategoryIds: filters.productCategoryIds || [],
            dateRange: filters.dateRange // Pass the date range to filter campaigns
          });
          
          // Transform the response data to the format expected by the Select component
          if (response && response.data) {
            const campaignOptions = response.data.map(campaign => ({
              label: campaign.code || campaign.name || `Campaign ${campaign.id}`,
              value: campaign.id
            }));
            
            setCampaignOptions(campaignOptions);
          }
        } catch (error) {
          console.error('Error fetching campaigns:', error);
          message.error('Failed to load campaign options');
        } finally {
          setLoadingFilter(null);
        }
      } else {
        // Clear campaign options if no brands are selected or no date range
        setCampaignOptions([]);
        // Also clear any selected campaigns
        if (filters.campaignIds?.length > 0) {
          handleFilterChange('campaignIds', []);
        }
      }
    };

    fetchCampaigns();
  }, [filters.brandIds, filters.productIds, filters.productCategoryIds, filters.dateRange]);

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    if (!filters?.dateRange || filters.dateRange.length !== 2) {
      message.warning('Please select a valid date range');
      return;
    }
    
    onApplyFilters({
      brandIds: filters?.brandIds || [],
      productIds: filters?.productIds || [],
      productCategoryIds: filters?.productCategoryIds || [],
      campaignIds: filters?.campaignIds || [],
      timePeriod: filters?.timePeriod || 'daily',
      startDate: filters.dateRange[0],
      endDate: filters.dateRange[1],
      dateRange: filters.dateRange, // Add dateRange to applied filters
    });
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const defaultFilters = {
      brandIds: [],
      productIds: [],
      productCategoryIds: [],
      campaignIds: [],
      dateRange: [dayjs(), dayjs().add(30, "days")],
      timePeriod: "daily",
    };
    
    setFilters(defaultFilters);
    onClearFilters();
  };

  return (
    <Card 
      size="small" 
      title={!filtersMinimized ? "Filters" : null}
      className="mt-2" 
      extra={
        <Button 
          type="text"
          icon={filtersMinimized ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => {
            const newState = !filtersMinimized;
            setFiltersMinimized(newState);
          }}
          size="small"
          title={filtersMinimized ? "Expand filters" : "Collapse filters"}
        />
      }
      style={{
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {filtersMinimized ? (
        <div className="text-center" style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', height: '100%', margin: 'auto' }}>
          <strong>Filters</strong>
        </div>
      ) : (
        <Form layout="vertical" className="px-3">
          {/* Date Range Filter */}
          <Form.Item label="Date Range" className="mb-3">
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates)}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          {/* Brand Filter */}
          <Form.Item label="Brands" className="mb-3">
            <Select
              mode="multiple"
              placeholder="Select brands"
              value={filters.brandIds}
              onChange={(value) => handleFilterChange('brandIds', value)}
              loading={loadingFilter === 'brands'}
              style={{ width: '100%' }}
              allowClear
            >
              {brandOptions.map(brand => (
                <Option key={brand.value} value={brand.value}>{brand.label}</Option>
              ))}
            </Select>
          </Form.Item>
          
          {/* Product Category Filter */}
          <Form.Item label="Product Categories" className="mb-3">
            <Select
              mode="multiple"
              placeholder="Select product categories"
              value={filters.productCategoryIds}
              onChange={(value) => handleFilterChange('productCategoryIds', value)}
              loading={loadingFilter === 'productCategories'}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {productCategoryOptions.map(category => (
                <Option key={category.value} value={category.value}>{category.label}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Product Filter */}
          <Form.Item label="Products" className="mb-3">
            {filters.brandIds?.length > 0 ? (
              <Select
                mode="multiple"
                placeholder="Select products"
                value={filters.productIds}
                onChange={(value) => handleFilterChange('productIds', value)}
                loading={loadingFilter === 'products'}
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {productOptions.map(product => (
                  <Option key={product.value} value={product.value}>{product.label}</Option>
                ))}
              </Select>
            ) : (
              <div className="text-muted">Select brands to view product options</div>
            )}
          </Form.Item>

          {/* Campaign Filter */}
          <Form.Item label="Campaigns" className="mb-3">
            {filters.brandIds?.length > 0 && filters.dateRange?.length === 2 ? (
              <Select
                mode="multiple"
                placeholder="Select campaigns"
                value={filters.campaignIds}
                onChange={(value) => handleFilterChange('campaignIds', value)}
                loading={loadingFilter === 'campaigns'}
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {campaignOptions.map(campaign => (
                  <Option key={campaign.value} value={campaign.value}>{campaign.label}</Option>
                ))}
              </Select>
            ) : (
              <div className="text-muted">Select brands and date range to view campaign options</div>
            )}
          </Form.Item>

          {/* Time Period Filter */}
          <Form.Item label="Time Period" className="mb-3">
            <Radio.Group
              style={{ width: '100%' }}
              value={filters.timePeriod}
              onChange={(e) => handleFilterChange('timePeriod', e.target.value)}
              optionType="button"
              buttonStyle="outline"
            >
              <Radio.Button value="daily">Daily</Radio.Button>
              <Radio.Button value="weekly">Weekly</Radio.Button>
              <Radio.Button value="monthly">Monthly</Radio.Button>
              <Radio.Button value="yearly">Yearly</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* Action Buttons */}
          <div className="d-flex gap-2 mt-4 mb-3">
            <Button 
              type="primary" 
              onClick={handleApplyFilters}
              icon={<FilterOutlined />}
            >
              Apply Filters
            </Button>
            <Button 
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </div>
        </Form>
      )}
    </Card>
  );
};

export default CashFlowFilters;
